import { AxiosResponse } from "axios";
import { APP_ROUTES, CONTENT_QUALITY_LEVELS, ENDPOINTS } from "const";
import { FormikHelpers } from "formik";
import { useIssue, useSolution, useUpdateAttachments } from "hooks";
import { useCallback, useMemo } from "react";
import { useMutation, UseMutationOptions, useQueryClient } from "react-query";
import { useLocation, useParams } from "react-router-dom";
import {
  IAISimilar,
  IAISuggestions,
  IAsset,
  IAttachment,
  ISolution,
} from "types";
import { showErrorMessage, showSuccessMessage } from "utils";
import { IManagementProps, SolutionQualityReviewMap } from "./SolutionForm.types";
import { createSolution, updateSolution } from "./SolutionForm.services";
import {
  FIELD_NAMES,
  SolutionInitial,
  SolutionPayload,
} from "./SolutionForm.constants";
import {
  createAISimilarSolutions,
  createAISuggestionsSolutions,
  IAISolutionProps,
} from "services";

export function useInitialState() {
  const location = useLocation();
  const isSolution = location.pathname.includes(APP_ROUTES.SOLUTION);
  const { id } = useParams<{ id: string }>();

  const { data: solutionData } = useSolution({
    id: id,
    enabled: !!id && isSolution,
  });

  return useMemo(() => {
    return new SolutionInitial(solutionData?.data);
  }, [solutionData?.data]);
}

interface IAIAnalyticsSolutionProps
  extends Partial<UseMutationOptions<AxiosResponse<IAISuggestions>, any, any>> {
  id?: string | undefined;
}

export function useCreateAIAnalyticsSolution({
  ...rest
}: IAIAnalyticsSolutionProps) {
  const queryClient = useQueryClient();
  return useMutation<AxiosResponse<IAISuggestions>, any, any>(
    (solution: IAISolutionProps) =>
      createAISuggestionsSolutions({ ...solution }),
    {
      mutationKey: ["createAIAnalyticsSolution"],
      onSuccess: (data) => {
        queryClient.setQueryData(
          ["createAIAnalyticsSolution"],
          data.data || {}
        );
      },
      ...rest,
    }
  );
}

interface IAISimilarSolutionProps
  extends Partial<UseMutationOptions<AxiosResponse<IAISimilar>, any, any>> {
  id?: string | undefined;
}

export function useCreateAISimilarSolution({
  ...rest
}: IAISimilarSolutionProps) {
  const queryClient = useQueryClient();
  return useMutation<AxiosResponse<IAISimilar>, any, any>(
    (solution: IAISolutionProps) => createAISimilarSolutions({ ...solution }),
    {
      mutationKey: ["createAISimilarSolution"],
      onSuccess: (data) => {
        queryClient.setQueryData(["createAISimilarSolution"], data.data || {});
      },

      ...rest,
    }
  );
}

interface ISolutionProps
  extends Partial<UseMutationOptions<AxiosResponse<ISolution>, any, any>> {
  solutionId?: string | undefined;
}

export function useCreateSolution({ ...rest }: ISolutionProps) {
  return useMutation<AxiosResponse<ISolution>, any, any>(
    (solution: ISolution) => createSolution({ solution }),
    {
      ...rest,
    }
  );
}

export function useUpdateSolution({
  solutionId = "",
  ...rest
}: ISolutionProps & {
  solutionId: string | undefined;
}) {
  return useMutation<AxiosResponse<ISolution>, any, any>(
    (solution: ISolution) => updateSolution({ solutionId, solution }),
    {
      ...rest,
    }
  );
}

export function useSubmitHandler(
  success: (id?: string) => void,
  manage: IManagementProps
) {
  const params = useParams<{ id: string }>();

  const { mutateAsync: mutateCreateAISuggestionSolutionAsync } =
    useCreateAIAnalyticsSolution({});
  const { mutateAsync: mutateCreateAISimilarSolutionAsync } =
    useCreateAISimilarSolution({});

  const { mutateAsync: mutateCreateSolutionAsync } = useCreateSolution({});
  const { mutateAsync: mutateUpdateSolutionAsync } = useUpdateSolution({
    solutionId: params.id,
  });

  const queryClient = useQueryClient();
  const location = useLocation();
  //on solution page - get issue id from backend
  const isSolution = location.pathname.includes(APP_ROUTES.SOLUTION);
  const { data: solutionData } = useSolution({
    id: params.id,
    enabled: !!params.id && isSolution,
  });
  const { data: issueData } = useIssue({
    id: params.id,
    enabled: !!params.id,
  });
  const { mutateAsync: updateAttachments } = useUpdateAttachments();

  const issueLocationDisplayLabel = issueData?.data?.location?.displayLabel;
  const issueLocationFormattedAddress =
    issueData?.data?.location?.formattedAddress;

  const qualityTrackedFields = useMemo(
    () => [
      FIELD_NAMES.TITLE,
      FIELD_NAMES.DESCRIPTION,
      FIELD_NAMES.EFFECTIVENESS,
    ],
    []
  );
  const qualityTrackedFieldSet = useMemo(
    () =>
      new Set(
        qualityTrackedFields.map((field) => field.toLocaleLowerCase())
      ),
    [qualityTrackedFields]
  );

  const normalize = useCallback((value?: string | null) => {
    if (!value) {
      return "";
    }
    const unicodeNormalized =
      typeof value.normalize === "function" ? value.normalize("NFKC") : value;
    return unicodeNormalized
      .trim()
      .replace(/\s+/g, " ")
      .replace(/[“”]/g, '"')
      .replace(/[‘’]/g, "'")
      .replace(/[\u2013\u2014\u2212]/g, "-")
      .replace(/([!?.,])\1+/g, "$1")
      .replace(/[!?.,]+$/g, "")
      .toLocaleLowerCase();
  }, []);

  const levenshteinDistance = useCallback((a: string, b: string) => {
    if (a === b) {
      return 0;
    }
    if (!a.length) {
      return b.length;
    }
    if (!b.length) {
      return a.length;
    }

    const lenB = b.length;
    const prev = Array.from({ length: lenB + 1 }, (_, index) => index);
    const curr = new Array<number>(lenB + 1);

    for (let i = 1; i <= a.length; i++) {
      curr[0] = i;
      const charA = a[i - 1];
      for (let j = 1; j <= lenB; j++) {
        const cost = charA === b[j - 1] ? 0 : 1;
        const insertion = curr[j - 1] + 1;
        const deletion = prev[j] + 1;
        const substitution = prev[j - 1] + cost;
        curr[j] = Math.min(insertion, deletion, substitution);
      }
      for (let j = 0; j <= lenB; j++) {
        prev[j] = curr[j];
      }
    }
    return prev[lenB];
  }, []);

  const isMinorEdit = useCallback(
    (previous: string, current: string) => {
      if (!previous || !current) {
        return false;
      }
      const maxEditDistance = 3;
      if (Math.abs(previous.length - current.length) > maxEditDistance) {
        return false;
      }
      const distance = levenshteinDistance(previous, current);
      if (distance > maxEditDistance) {
        return false;
      }
      const relativeChange = distance / Math.max(previous.length, current.length);
      return relativeChange <= 0.2;
    },
    [levenshteinDistance]
  );

  return useCallback(
    async (
      solution: SolutionInitial,
      formikHelpers: FormikHelpers<SolutionInitial>
    ) => {
      formikHelpers.setSubmitting(true);
      const invalidate = () => {
        queryClient.invalidateQueries([ENDPOINTS.SOLUTIONS]);
        queryClient.invalidateQueries([ENDPOINTS.SOLUTION_RATING]);
        queryClient.invalidateQueries([ENDPOINTS.ISSUES]);
        queryClient.invalidateQueries([ENDPOINTS.ISSUE]);
        queryClient.invalidateQueries([ENDPOINTS.MAP_LOCATIONS]);
      };

      const isSolution = location.pathname.includes(APP_ROUTES.SOLUTION);
      const isIssue = location.pathname.includes(APP_ROUTES.ISSUE);
      let saved: any;

      const updateScenario = !!params.id && isSolution;

      try {
        if (params.id) {
          const issueId = isSolution ? solutionData?.data?.issue?.id : params.id;
          const payload = new SolutionPayload(
            { ...solution, attachmentsType: "solutions" },
            issueId
          );

          const solutionPayload = payload.getSolution();

          const solutionAnalyticsPayload = {
            solution_title: solutionPayload?.title,
            solution_details: solutionPayload?.description,
            solution_effectiveness: solutionPayload?.effectiveness,
            issue_title: issueData?.data?.title,
            issue_location:
              issueLocationDisplayLabel ||
              issueLocationFormattedAddress ||
              "",
            issue_details: issueData?.data?.details,
            docs: solution?.attachments
              ?.filter((a) => a.type === "file")
              ?.map((a) => (a as unknown as IAttachment<IAsset>)?.asset?.url),
            links: solution?.attachments
              ?.filter((a) => a.type === "link")
              ?.map((a) => (a as unknown as IAttachment)?.data),
            labels: solution?.tags,
          };
          const qualityCache = manage.qualityReviewRef?.current;

          const shouldEvaluateField = (field: FIELD_NAMES) => {
            if (!qualityCache) {
              return true;
            }
            const cached = qualityCache[field];
            if (!cached) {
              return true;
            }
            const isHighQuality =
              cached.quality === CONTENT_QUALITY_LEVELS.HIGH ||
              cached.quality === CONTENT_QUALITY_LEVELS.GOOD;
            if (!isHighQuality) {
              return true;
            }
            const currentValue = (solution[field] as string) || "";
            const cachedNormalized = normalize(cached.user_input);
            const currentNormalized = normalize(currentValue);

            if (cachedNormalized === currentNormalized) {
              return false;
            }

            return !isMinorEdit(cachedNormalized, currentNormalized);
          };

          const fieldsToEvaluate = qualityTrackedFields.filter((field) =>
            shouldEvaluateField(field)
          );
          const fieldsToEvaluateSet = new Set(
            fieldsToEvaluate.map((field) => field.toLocaleLowerCase())
          );

          const requireAISuggestions =
            manage?.proceedWithSuggestions !== true &&
            fieldsToEvaluate.length > 0;

          let aiSuggestionsReviewResult: Pick<
            AxiosResponse<IAISuggestions>,
            "data"
          >;

          if (manage?.proceedWithSuggestions === true) {
            aiSuggestionsReviewResult = {
              data: {
                entity: "solution",
                feedback_areas: [],
                more_feedback: {
                  quality: CONTENT_QUALITY_LEVELS.HIGH,
                  feedback: "",
                },
              },
            };
          } else if (!requireAISuggestions) {
            aiSuggestionsReviewResult = {
              data: {
                entity: "solution",
                feedback_areas: qualityCache
                  ? Object.values(qualityCache)
                  : [],
                more_feedback: {
                  quality: CONTENT_QUALITY_LEVELS.HIGH,
                  feedback: "",
                },
              },
            };
          } else {
            const aiResponse = await mutateCreateAISuggestionSolutionAsync(
              solutionAnalyticsPayload
            );
            aiSuggestionsReviewResult = { data: aiResponse.data };
          }

          const feedbackAreas =
            aiSuggestionsReviewResult.data.feedback_areas || [];
          const trackedFeedbackAreas = feedbackAreas.filter((area) =>
            qualityTrackedFieldSet.has(area.input_name.toLocaleLowerCase())
          );

          const baseCache: SolutionQualityReviewMap = qualityCache
            ? { ...qualityCache }
            : {};
          const nextQualityCache: SolutionQualityReviewMap = { ...baseCache };

          trackedFeedbackAreas.forEach((area) => {
            const key = area.input_name.toLocaleLowerCase();
            if (fieldsToEvaluateSet.has(key) || !nextQualityCache[key]) {
              nextQualityCache[key] = area;
            }
          });

          const mergedFeedbackAreas = qualityTrackedFields
            .map((field) => {
              const key = field.toLocaleLowerCase();
              return nextQualityCache[key];
            })
            .filter((area): area is NonNullable<typeof area> => !!area);

          if (manage.qualityReviewRef) {
            manage.qualityReviewRef.current = nextQualityCache;
          }

          const sanitizedResult: IAISuggestions = {
            ...aiSuggestionsReviewResult.data,
            feedback_areas:
              mergedFeedbackAreas.length > 0
                ? mergedFeedbackAreas
                : trackedFeedbackAreas,
          };

          formikHelpers.setFieldValue("suggestedResult", sanitizedResult);

          if (
            manage?.proceedWithSuggestions === false &&
            ((aiSuggestionsReviewResult.data.feedback_areas.length > 0 &&
              aiSuggestionsReviewResult.data.feedback_areas.find(
                (area) =>
                  area.quality !== CONTENT_QUALITY_LEVELS.GOOD &&
                  area.quality !== CONTENT_QUALITY_LEVELS.HIGH
              )) ||
              aiSuggestionsReviewResult.data?.more_feedback?.quality ===
                "Useless" ||
              aiSuggestionsReviewResult.data?.more_feedback?.quality === "Poor")
          ) {
            formikHelpers.setTouched({
              title: false,
              description: false,
              effectiveness: false,
            });
            document.querySelector("#solution-report")?.scrollTo(0, 0);
            formikHelpers.setSubmitting(false);
            return;
          }

          let aiSimilarSolutionsResult: {
            data: { potential_similarity_id: string[] };
          };
          if (manage?.proceedWithSimilar === true) {
            aiSimilarSolutionsResult = {
              data: { potential_similarity_id: [] },
            };
          } else {
            try {
              const response = await mutateCreateAISimilarSolutionAsync(
                solutionAnalyticsPayload
              );
              aiSimilarSolutionsResult = { data: response.data };
            } catch (similarError) {
              console.warn(
                "Solution similarity check failed, continuing without blocking",
                similarError
              );
              aiSimilarSolutionsResult = {
                data: { potential_similarity_id: [] },
              };
            }
          }

          if (
            !updateScenario &&
            manage?.proceedWithSimilar === false &&
            aiSimilarSolutionsResult?.data?.potential_similarity_id.length > 0
          ) {
            return;
          }

          if (manage.showSolutionFields && isSolution) {
            saved = await mutateUpdateSolutionAsync(payload.getSolution());
            showSuccessMessage("Solution is updated!");
          }
          if (manage.showSolutionFields && isIssue) {
            saved = await mutateCreateSolutionAsync(payload.getSolution());
            showSuccessMessage("Solution is created!");
          }
          if (saved?.data?.id) {
            await updateAttachments(payload.getAttachments(saved.data.id));
            showSuccessMessage("Attachments are updated!");
          }
         success(saved?.data.id);
          invalidate();
          queryClient.getMutationCache().clear();
          formikHelpers.setSubmitting(false);
          formikHelpers.setFieldValue("suggestedResult", undefined);
          if (manage.qualityReviewRef) {
            manage.qualityReviewRef.current = {};
          }
        }
      } catch (error: any) {
        formikHelpers.setSubmitting(false);
        if (error?.response?.data) {
          formikHelpers.setErrors(error.response.data);
        }
        showErrorMessage("Error occurred! Check form!");
      }
    },
    [
      isMinorEdit,
      issueData?.data?.details,
      issueData?.data?.title,
      issueLocationDisplayLabel,
      issueLocationFormattedAddress,
      location.pathname,
      manage?.proceedWithSimilar,
      manage?.proceedWithSuggestions,
      manage?.qualityReviewRef,
      manage.showSolutionFields,
      mutateCreateAISimilarSolutionAsync,
      mutateCreateAISuggestionSolutionAsync,
      mutateCreateSolutionAsync,
      mutateUpdateSolutionAsync,
      normalize,
      params.id,
      queryClient,
      qualityTrackedFieldSet,
      qualityTrackedFields,
      solutionData?.data?.issue?.id,
      success,
      updateAttachments,
    ]
  );
}
